/**
 * Utility functions for URL processing
 */

/**
 * Checks if the URL should have a prefix added based on the current context
 * @returns {boolean} Whether a prefix should be added
 */
export const shouldAddPrefix = (): boolean =>
  !!(
    window.veeqoContext &&
    window.veeqoContext.vqHost !== 'standalone' &&
    window.veeqoContext.baseUrl
  );

/**
 * Checks if a URL is relative (not starting with http://, https://, or //)
 * @param {string} url - The URL to check
 * @returns {boolean} Whether the URL is relative
 */
export const isRelativeUrl = (url: string): boolean => !url.match(/^(https?:\/\/|\/\/)/);

/**
 * Checks if a URL is an anchor link (starts with #)
 * @param {string} url - The URL to check
 * @returns {boolean} Whether the URL is an anchor link
 */
export const isAnchorUrl = (url: string): boolean => url.startsWith('#');

/**
 * Adds the appropriate prefix to a URL based on the current context
 * @param {string} url - The URL to add a prefix to
 * @returns {string} The URL with prefix added if needed
 */
export const addPrefixToUrl = (url: string): string =>
  `${window.veeqoContext?.baseUrl || ''}${url.startsWith('/') ? '' : '/'}${url}`;

/**
 * Processes a URL by adding the appropriate prefix if needed
 * @param {string} url - The URL to process
 * @returns {string} The processed URL
 */
export const createContextAwareUrl = (url: string | undefined | null): string => {
  if (!url) return '';
  if (isAnchorUrl(url) || !shouldAddPrefix() || !isRelativeUrl(url)) return url;

  return addPrefixToUrl(url);
};
